import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Trash2 } from 'lucide-react';
import { z } from 'zod';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { SelectItem } from '@/common/components/ui/select';
import { useCountries } from '@/features/auth/hooks/useCountries';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { FormLocationField } from '@/features/geolocation/components/form-location-field/FormLocationField';
import { EMPTY_LOCATION_VALUE, type LocationValue } from '@/features/geolocation/types/location';
import { parseCoordinateString } from '@/features/geolocation/utils/coordinates';
import { toSupplyPointGeoPayload } from '../../mappers/toSupplyPointGeoPayload';
import { useSupplyPoint } from '../../context/useSupplyPoint';
import { supplyPointQueries, supplyPointQueryKeys } from '../../queries';
import { SupplyPointService } from '../../services/SupplyPointService';
import type { SupplyPoint } from '../../services/schemas';
import { resolveActiveSupplyPointId } from '../../utils/resolveActiveSupplyPoint';

// ─── Form schema ──────────────────────────────────────────────────────────────

const detailsFormSchema = z.object({
  name: requiredInput(z.string().min(1).max(255)),
  street: requiredInput(z.string().min(1).max(255)),
  city: requiredInput(z.string().min(1).max(100)),
  zipCode: requiredInput(z.string().min(1).max(20)),
  country: requiredInput(z.string()),
  systemType: requiredInput(z.coerce.number().int().positive()),
  // The position is optional — a missing one only degrades production prediction.
  location: z
    .object({
      latitude: z.number().min(-90).max(90).nullable(),
      longitude: z.number().min(-180).max(180).nullable(),
      altitude: z.number().min(-9999).max(9999).nullable(),
      source: z.enum(['stored', 'geocoded', 'manual']).nullable(),
      altitudeSource: z.enum(['auto', 'manual']).nullable(),
    })
    .refine((value) => (value.latitude === null) === (value.longitude === null), {
      // A function, so the message is translated when validation runs rather than
      // at module load, before a locale is active.
      error: () => t`Latitude and longitude must be set together`,
    }),
});

/** Stored coordinates are authoritative, so they load in as an already-pinned position. */
function toStoredLocation(supplyPoint: SupplyPoint | null): LocationValue {
  const latitude = parseCoordinateString(supplyPoint?.latitude);
  const longitude = parseCoordinateString(supplyPoint?.longitude);

  if (latitude === null || longitude === null) return EMPTY_LOCATION_VALUE;

  return {
    latitude,
    longitude,
    altitude: parseCoordinateString(supplyPoint?.altitude),
    source: 'stored',
    altitudeSource: 'auto',
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DetailsTab() {
  const { activeSupplyPoint, setActiveSupplyPointId } = useSupplyPoint();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { data: types } = useSuspenseQuery(supplyPointQueries.types());
  const countries = useCountries();
  /** Bumped on address-field blur; arms the picker's debounced auto-locate. */
  const [locateSignal, setLocateSignal] = useState(0);
  const armLocate = () => setLocateSignal((signal) => signal + 1);

  const form = useAppForm(
    appFormOptions({
      schema: detailsFormSchema,
      defaultValues: {
        name: activeSupplyPoint?.name ?? null,
        street: activeSupplyPoint?.street ?? null,
        city: activeSupplyPoint?.city ?? null,
        zipCode: activeSupplyPoint?.zipCode ?? null,
        country: activeSupplyPoint?.country ?? 'CZ',
        systemType: activeSupplyPoint ? String(activeSupplyPoint.systemType) : null,
        location: toStoredLocation(activeSupplyPoint ?? null),
      },
      onSubmit: async ({ value }) => {
        if (!activeSupplyPoint) return;
        // Explicit payload: spreading `value` would PATCH a stray `location` object.
        await SupplyPointService.partialUpdate(activeSupplyPoint.id, {
          name: value.name,
          street: value.street,
          city: value.city,
          zipCode: value.zipCode,
          country: value.country,
          systemType: value.systemType,
          ...toSupplyPointGeoPayload(value.location),
        });
        await queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all });
        toast.success(t`Settings saved`);
      },
    })
  );

  // Primitive subscriptions, so the picker's address prop is stable until one of
  // these four fields actually changes.
  const street = useStore(form.store, (state) => state.values.street);
  const city = useStore(form.store, (state) => state.values.city);
  const zipCode = useStore(form.store, (state) => state.values.zipCode);
  const country = useStore(form.store, (state) => state.values.country);

  const deleteMutation = useMutation({
    mutationFn: () => SupplyPointService.delete(activeSupplyPoint!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all });
      const remaining = await queryClient.ensureQueryData(supplyPointQueries.list());
      const nextId = resolveActiveSupplyPointId(remaining, null);
      if (nextId !== null) {
        setActiveSupplyPointId(nextId);
        navigate({ to: '/', replace: true });
      } else {
        navigate({ to: '/no-supply-points', replace: true });
      }
    },
  });

  const handleDelete = async () => {
    if (!activeSupplyPoint) return;
    const supplyPointName = activeSupplyPoint.name;
    const confirmed = await confirm({
      title: t`Delete supply point?`,
      description: t`"${supplyPointName}" will be permanently deleted along with its devices, products, and access. This action is irreversible.`,
      confirmLabel: t`Delete supply point`,
    });
    if (confirmed) deleteMutation.mutate();
  };

  return (
    <div className="max-w-4xl">
      <form.AppForm>
        <form.FormElement>
          <div className="space-y-6 mt-6">
            <FormErrors />

            {/* Location & identification */}
            <div className="rounded-xl border border-border bg-card">
              <div className="px-6 pt-6 pb-4 border-b border-border/50">
                <h3 className="font-semibold leading-none tracking-tight">
                  <Trans>Location & identification</Trans>
                </h3>
                <p className="text-sm text-muted-foreground mt-1.5">
                  <Trans>Basic identification and address of the supply point.</Trans>
                </p>
              </div>

              <div className="px-6 pt-6 pb-6 space-y-6">
                <form.AppField name="name">
                  {(field) => <field.TextField label={<Trans>Supply point name</Trans>} required />}
                </form.AppField>

                <form.AppField name="street" listeners={{ onBlur: () => armLocate() }}>
                  {(field) => <field.TextField label={<Trans>Street and number</Trans>} required />}
                </form.AppField>

                <div className="grid grid-cols-3 gap-4">
                  <form.AppField name="city" listeners={{ onBlur: () => armLocate() }}>
                    {(field) => <field.TextField label={<Trans>City</Trans>} required />}
                  </form.AppField>

                  <form.AppField name="zipCode" listeners={{ onBlur: () => armLocate() }}>
                    {(field) => <field.TextField label={<Trans>Postal code</Trans>} required />}
                  </form.AppField>

                  {/* A select never blurs on the way to a new value — the
                      choice itself is the moment the field settled, so this
                      arms on onChange rather than pretending it's a blur. */}
                  <form.AppField name="country" listeners={{ onChange: () => armLocate() }}>
                    {(field) => (
                      <field.SelectField label={<Trans>Country</Trans>} required>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                </div>

                <form.AppField name="location">
                  {() => (
                    <FormLocationField
                      label={<Trans>Exact position</Trans>}
                      description={
                        <Trans>
                          Check the position on the map and correct it if needed. It is used for
                          production prediction.
                        </Trans>
                      }
                      address={{
                        street,
                        city,
                        postalcode: zipCode,
                        country,
                      }}
                      locateSignal={locateSignal}
                    />
                  )}
                </form.AppField>
              </div>
            </div>

            {/* Technical configuration */}
            <div className="rounded-xl border border-border bg-card">
              <div className="px-6 pt-6 pb-4 border-b border-border/50">
                <h3 className="font-semibold leading-none tracking-tight">
                  <Trans>Technical configuration</Trans>
                </h3>
                <p className="text-sm text-muted-foreground mt-1.5">
                  <Trans>Integration type settings.</Trans>
                </p>
              </div>

              <div className="px-6 pt-6 pb-6 space-y-6">
                <form.AppField name="systemType">
                  {(field) => (
                    <field.SelectField label={<Trans>Integration type</Trans>} required>
                      {types.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </field.SelectField>
                  )}
                </form.AppField>
              </div>
            </div>

            <div className="flex justify-end">
              <FormSubmit>
                <Trans>Save changes</Trans>
              </FormSubmit>
            </div>
          </div>
        </form.FormElement>
      </form.AppForm>

      {activeSupplyPoint?.authorizedRole === 'full' && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 mt-6">
          <h4 className="text-sm font-semibold text-destructive mb-1">
            <Trans>Danger zone</Trans>
          </h4>
          <p className="text-sm text-destructive/80 mb-4">
            <Trans>
              Deleting the supply point will remove all its devices, products, and access. This
              action is irreversible.
            </Trans>
          </p>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
          >
            <Trash2 size={14} />
            <Trans>Delete supply point</Trans>
          </button>
        </div>
      )}
    </div>
  );
}
